'use client';

import { useEffect, useRef } from 'react';

interface KaleidoscopeBackgroundProps {
  segments?: number;
  speed?: number;
  opacity?: number;
}

export default function KaleidoscopeBackground({
  segments = 12,
  speed = 0.3,
  opacity = 0.4,
}: KaleidoscopeBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    // Set canvas size
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // Color palette that shifts over time
    const getColor = (angle: number, time: number, index: number) => {
      const hue = (angle + time * 20 + index * 30) % 360;
      const saturation = 70 + Math.sin(time * 0.5 + index) * 20;
      const lightness = 50 + Math.cos(time * 0.3 + index * 0.5) * 15;
      return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    };

    // Draw kaleidoscope
    const draw = () => {
      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.max(width, height) * 0.7;

      ctx.clearRect(0, 0, width, height);

      // Save the current context
      ctx.save();
      ctx.globalAlpha = opacity;

      // Draw each segment
      const angleStep = (Math.PI * 2) / segments;

      for (let i = 0; i < segments; i++) {
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(angleStep * i + timeRef.current * speed);

        // Create gradient for this segment
        const gradient = ctx.createLinearGradient(0, 0, radius, 0);

        const color1 = getColor(i * angleStep * (180 / Math.PI), timeRef.current, i);
        const color2 = getColor(i * angleStep * (180 / Math.PI) + 60, timeRef.current, i + 1);
        const color3 = getColor(i * angleStep * (180 / Math.PI) + 120, timeRef.current, i + 2);

        gradient.addColorStop(0, color1);
        gradient.addColorStop(0.5, color2);
        gradient.addColorStop(1, color3);

        // Draw triangular segment
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(radius * Math.cos(-angleStep / 2), radius * Math.sin(-angleStep / 2));
        ctx.lineTo(radius * Math.cos(angleStep / 2), radius * Math.sin(angleStep / 2));
        ctx.closePath();

        ctx.fillStyle = gradient;
        ctx.fill();

        // Add some geometric patterns within the segment
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.1 + Math.sin(timeRef.current + i) * 0.05})`;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Add inner circles
        for (let j = 1; j <= 3; j++) {
          const innerRadius = (radius / 4) * j;
          ctx.beginPath();
          ctx.arc(0, 0, innerRadius, -angleStep / 2, angleStep / 2);
          ctx.strokeStyle = `rgba(255, 255, 255, ${0.05 + Math.sin(timeRef.current * 2 + i + j) * 0.03})`;
          ctx.lineWidth = 1;
          ctx.stroke();
        }

        ctx.restore();
      }

      // Add central glow
      const centerGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 200);
      centerGradient.addColorStop(0, `hsla(${(timeRef.current * 50) % 360}, 80%, 70%, 0.3)`);
      centerGradient.addColorStop(0.5, `hsla(${(timeRef.current * 50 + 120) % 360}, 70%, 60%, 0.1)`);
      centerGradient.addColorStop(1, 'transparent');

      ctx.fillStyle = centerGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 200, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();

      // Update time
      timeRef.current += 0.01;

      // Continue animation
      animationRef.current = requestAnimationFrame(draw);
    };

    // Start animation
    draw();

    return () => {
      window.removeEventListener('resize', setCanvasSize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [segments, speed, opacity]);

  return (
    <canvas data-component-id="src/components/effects/KaleidoscopeBackground.tsx:136:4" data-component-path="src/components/effects/KaleidoscopeBackground.tsx" data-component-line="136" data-component-end-line="145" data-component-file="KaleidoscopeBackground.tsx" data-component-name="canvas" data-component-content="%7B%22elementName%22%3A%22canvas%22%2C%22className%22%3A%22absolute%20inset-0%20pointer-events-none%22%7D"
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{
        // mixBlendMode: 'screen',
        // filter: 'blur(40px)',
        width: '100%',
        height: '100%',
      }}
    />
  );
}
